import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import API_CONFIG from '../utils/apiConfig';
import { formatPaymentNetPayable } from '../utils/paymentDisplay';
import { subcontractorDashboardOrgTitle } from '../utils/userDashboardUi';
import { useDashboardNav } from '../context/DashboardNavContext';
import './SubcontractorDashboard.css';
import '../styles/pagination-mobile.css';

/** Read deep-link query for first paint (avoids fetching wrong tab before state updates). */
function parseSubcontractorDashboardSearch(search) {
  try {
    const params = new URLSearchParams(search || '');
    const menu = (params.get('menu') || '').toLowerCase();
    const tab = (params.get('tab') || '').toLowerCase();
    return {
      menu: menu === 'employers' || menu === 'payments' ? menu : 'payments',
      tab: tab === 'pending' || tab === 'approved' ? tab : 'approved',
    };
  } catch {
    return { menu: 'payments', tab: 'approved' };
  }
}

const SubcontractorDashboard = ({ user, onLogout }) => {
  const location = useLocation();
  // Local user state to handle updates after profile changes
  const [localUser, setLocalUser] = useState(user);
  
  // Sync localUser with prop user when it changes
  useEffect(() => {
    if (user) {
      setLocalUser(user);
    }
  }, [user]);
  
  // Also sync from localStorage when user-updated event fires or on mount
  useEffect(() => {
    const handleUserUpdated = () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        try {
          const userData = JSON.parse(userJson);
          setLocalUser(userData);
        } catch (err) {
          console.error('Error parsing user data:', err);
        }
      }
    };
    
    // Check localStorage on mount
    handleUserUpdated();
    
    window.addEventListener('user-updated', handleUserUpdated);
    return () => window.removeEventListener('user-updated', handleUserUpdated);
  }, []);
  
  // Also listen for storage events (cross-tab updates)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user' && e.newValue) {
        try {
          const userData = JSON.parse(e.newValue);
          setLocalUser(userData);
        } catch (err) {
          console.error('Error parsing user data from storage:', err);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  // Use localUser for checks to ensure we have latest data
  const currentUser = localUser || user;

  const subcontractorDashboardTitle = useMemo(
    () => subcontractorDashboardOrgTitle(currentUser),
    [currentUser]
  );

  const { setDashboardNav, clearDashboardNav } = useDashboardNav();

  const [allEmployers, setAllEmployers] = useState([]);
  const [filteredEmployers, setFilteredEmployers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [connectedEmployers, setConnectedEmployers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [relationshipsLoading, setRelationshipsLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState(() =>
    parseSubcontractorDashboardSearch(location.search).menu
  );
  const [mobileBottomNav, setMobileBottomNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [activeTab, setActiveTab] = useState(() =>
    parseSubcontractorDashboardSearch(location.search).tab
  );

  const subcontractorSectionTitle = useMemo(() => {
    if (activeMenu === 'employers') return 'My Employers';
    if (activeMenu === 'payments') return 'Payments';
    return null;
  }, [activeMenu]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContractTermsModalOpen, setIsContractTermsModalOpen] = useState(false);
  const [contractTerms, setContractTerms] = useState('');
  const [employerName, setEmployerName] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [gdprConsentGiven, setGdprConsentGiven] = useState(false);
  const [loadingTerms, setLoadingTerms] = useState(false);
  /** When set, contract modal confirms acceptance of employer invite (not a new connection request). */
  const [acceptInviteRelationshipId, setAcceptInviteRelationshipId] = useState(null);
  /** Re-open Find Employers modal after closing terms only when terms were opened from that flow. */
  const [contractModalFromFindEmployers, setContractModalFromFindEmployers] = useState(false);
  const [employerInfoModalOpen, setEmployerInfoModalOpen] = useState(false);
  const [employerInfoLoading, setEmployerInfoLoading] = useState(false);
  const [employerInfoData, setEmployerInfoData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentSubTab, setPaymentSubTab] = useState('list'); // 'list' | 'monthly'
  const [monthlyStatementsYear, setMonthlyStatementsYear] = useState(new Date().getFullYear());
  const [monthlyStatementsItems, setMonthlyStatementsItems] = useState([]);
  const [monthlyStatementsLoading, setMonthlyStatementsLoading] = useState(false);
  const [monthlyStatementDownloading, setMonthlyStatementDownloading] = useState(null); // 'year-month' while downloading
  // Pagination state
  const [employersPage, setEmployersPage] = useState(1);
  const [employersPerPage, setEmployersPerPage] = useState(100);
  const [employersTotal, setEmployersTotal] = useState(0);
  const [employersTotalPages, setEmployersTotalPages] = useState(0);
  /** Tab badge counts from API totals (not current page length — pending is a separate fetch when on Approved). */
  const [approvedEmployersCount, setApprovedEmployersCount] = useState(0);
  const [pendingEmployersCount, setPendingEmployersCount] = useState(0);
  /** Pending relationships where employer invited sub — sub must review & accept terms */
  const [pendingEmployerTermsActionCount, setPendingEmployerTermsActionCount] = useState(0);

  // Deep-link support: ?menu=payments|employers&tab=pending|approved; default menu is payments
  useEffect(() => {
    const { menu, tab } = parseSubcontractorDashboardSearch(location.search);
    setActiveMenu(menu);
    setActiveTab(tab);
    setEmployersPage(1);
  }, [location.search]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = () => setMobileBottomNav(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsPerPage, setPaymentsPerPage] = useState(100);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsTotalPages, setPaymentsTotalPages] = useState(0);
  const [isPaymentRequestModalOpen, setIsPaymentRequestModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isPaymentDetailModalOpen, setIsPaymentDetailModalOpen] = useState(false);
  const [isWithdrawConfirmModalOpen, setIsWithdrawConfirmModalOpen] = useState(false);
  const [paymentToWithdraw, setPaymentToWithdraw] = useState(null);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [paymentRequestStep, setPaymentRequestStep] = useState(1); // 1 = select employer, 2 = payment type, 3 = job rate form, 4 = by day form, 5 = review
  const [showMaterials, setShowMaterials] = useState(false); // Track if materials section is expanded
  const [selectedPaymentEmployer, setSelectedPaymentEmployer] = useState(null);
  const [paymentType, setPaymentType] = useState('');
  const [isPaymentEmployerDropdownOpen, setIsPaymentEmployerDropdownOpen] = useState(false);
  const [paymentEmployerSearchQuery, setPaymentEmployerSearchQuery] = useState('');
  const [verificationChecked, setVerificationChecked] = useState(false);
  const [paymentVatRate, setPaymentVatRate] = useState(20);
  const [reverseChargeSuggestion, setReverseChargeSuggestion] = useState(false);
  const [showUtrModal, setShowUtrModal] = useState(false);
  const [utrModalUtr, setUtrModalUtr] = useState('');
  const [utrSaveLoading, setUtrSaveLoading] = useState(false);
  const [jobRateFormData, setJobRateFormData] = useState({
    workStartDate: '',
    description: '',
    jobCode: '',
    amount: ''
  });
  const [byDayFormData, setByDayFormData] = useState({
    weekCommencing: '',
    days: {
      monday: { description: '', jobCode: '', amount: '' },
      tuesday: { description: '', jobCode: '', amount: '' },
      wednesday: { description: '', jobCode: '', amount: '' },
      thursday: { description: '', jobCode: '', amount: '' },
      friday: { description: '', jobCode: '', amount: '' },
      saturday: { description: '', jobCode: '', amount: '' },
      sunday: { description: '', jobCode: '', amount: '' }
    }
  });
  const [materialsData, setMaterialsData] = useState([
    { name: '', job_code: '', description: '', amount: '' }
  ]);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const workStartDateRef = useRef(null);
  const weekCommencingRef = useRef(null);
  const paymentEmployerDropdownRef = useRef(null);
  const paymentEmployerInputRef = useRef(null);
  const jobRateMaterialsSectionRef = useRef(null);
  const byDayMaterialsSectionRef = useRef(null);

  // Fetch directory list once; relationships load via [activeMenu, activeTab, ...] with correct tab (no race with URL deep-link).
  useEffect(() => {
    fetchAllEmployers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (paymentEmployerDropdownRef.current && !paymentEmployerDropdownRef.current.contains(event.target)) {
        setIsPaymentEmployerDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isModalOpen || isContractTermsModalOpen || isPaymentRequestModalOpen || isPaymentDetailModalOpen || isWithdrawConfirmModalOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isModalOpen, isContractTermsModalOpen, isPaymentRequestModalOpen, isPaymentDetailModalOpen, isWithdrawConfirmModalOpen]);

  /** Active (connected) employers for payment request flow — independent of Employers tab fetches */
  const fetchActiveConnectedEmployersForPayments = useCallback(async () => {
    try {
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getRelationships));
      url.searchParams.append('page', '1');
      url.searchParams.append('per_page', '100');
      url.searchParams.append('status_filter', 'active');
      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
      if (!response.ok) return;
      const data = await response.json();
      const employers = data.items || [];
      setConnectedEmployers(employers.filter((emp) => emp.status === 'active'));
    } catch {
      /* ignore */
    }
  }, []);

  // Fetch payments when payments menu is active or pagination changes
  useEffect(() => {
    if (activeMenu === 'payments') {
      fetchPayments();
    }
  }, [activeMenu]);

  // Load connected employers for Request Payment (Employers tab fetch does not run on Payments menu)
  useEffect(() => {
    if (activeMenu === 'payments') {
      fetchActiveConnectedEmployersForPayments();
    }
  }, [activeMenu, fetchActiveConnectedEmployersForPayments]);

  // Fetch monthly payment statements when on Monthly statements sub-tab
  useEffect(() => {
    if (activeMenu !== 'payments' || paymentSubTab !== 'monthly') return;
    const fetchMonthlyStatements = async () => {
      setMonthlyStatementsLoading(true);
      try {
        const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.listMonthlyPaymentStatements));
        url.searchParams.append('year', monthlyStatementsYear);
        url.searchParams.append('view_mode', 'subcontractor');
        const res = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
        if (res.ok) {
          const data = await res.json();
          setMonthlyStatementsItems(data.items || []);
        } else {
          setMonthlyStatementsItems([]);
        }
      } catch (e) {
        if (e.message !== 'Token expired') setMonthlyStatementsItems([]);
      } finally {
        setMonthlyStatementsLoading(false);
      }
    };
    fetchMonthlyStatements();
  }, [activeMenu, paymentSubTab, monthlyStatementsYear]);

  // Fetch relationships when tab or pagination changes
  useEffect(() => {
    if (activeMenu === 'employers') {
      fetchRelationships();
    }
  }, [activeMenu, activeTab]);

  // Reset page when tab changes
  useEffect(() => {
    setEmployersPage(1);
  }, [activeTab]);

  const fetchAllEmployers = async () => {
    setLoading(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.searchEmployers),
        { method: 'GET' }
      );

      if (response.ok) {
        const data = await response.json();
        const selfId = currentUser?.user_id != null ? String(currentUser.user_id) : '';
        const employers = (data.employers || []).filter(
          (emp) => !selfId || String(emp.employer_id) !== selfId
        );
        setAllEmployers(employers);
        setFilteredEmployers(employers);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to load employers' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedEmployer(null);

    if (query.trim() === '') {
      setFilteredEmployers(allEmployers);
    } else {
      const filtered = allEmployers.filter(employer => {
        const name = (employer.name || '').toLowerCase();
        const email = (employer.email || '').toLowerCase();
        const searchLower = query.toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower);
      });
      setFilteredEmployers(filtered);
    }

    setIsDropdownOpen(true);
  };

  const handleSelectEmployer = (employer) => {
    setSelectedEmployer(employer);
    setSearchQuery(employer.name || employer.email);
    setIsDropdownOpen(false);
  };

  const handleOpenEmployerInfo = async (employer) => {
    setEmployerInfoLoading(true);
    setEmployerInfoModalOpen(true);
    setEmployerInfoData(null);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.getEmployerContractTerms}/${employer.employer_id}/contract-terms`),
        { method: 'GET' }
      );
      if (response.ok) {
        const data = await response.json();
        setEmployerInfoData(data);
      } else {
        setEmployerInfoData({
          employer_company_name: employer.employer_name,
          employer_id: employer.employer_id,
          error: true,
        });
      }
    } catch (e) {
      setEmployerInfoData({
        employer_company_name: employer.employer_name,
        employer_id: employer.employer_id,
        error: true,
      });
    } finally {
      setEmployerInfoLoading(false);
    }
  };

  const handleStartAcceptEmployerInvite = async (employerRow) => {
    if (!employerRow.relationship_id || !employerRow.employer_id) return;
    setContractModalFromFindEmployers(false);
    setAcceptInviteRelationshipId(employerRow.relationship_id);
    setSelectedEmployer({
      employer_id: employerRow.employer_id,
      name: employerRow.employer_name,
    });
    setLoadingTerms(true);
    setTermsAgreed(false);
    setGdprConsentGiven(false);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.getEmployerContractTerms}/${employerRow.employer_id}/contract-terms`),
        { method: 'GET' }
      );
      if (response.ok) {
        const data = await response.json();
        setContractTerms(data.contract_terms || '');
        setEmployerName(data.employer_company_name || data.employer_name || employerRow.employer_name);
      } else {
        setContractTerms('');
        setEmployerName(employerRow.employer_name || 'Employer');
      }
      setIsContractTermsModalOpen(true);
    } catch (error) {
      setContractTerms('');
      setEmployerName(employerRow.employer_name || 'Employer');
      setIsContractTermsModalOpen(true);
    } finally {
      setLoadingTerms(false);
    }
  };

  const fetchPendingEmployersSummary = async () => {
    try {
      let termsNeedingAction = 0;
      let total = 0;
      let page = 1;
      const perPage = 100;
      const maxPages = 10;

      while (page <= maxPages) {
        const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getRelationships));
        url.searchParams.append('page', String(page));
        url.searchParams.append('per_page', String(perPage));
        url.searchParams.append('status_filter', 'pending');
        const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
        if (!response.ok) break;
        const data = await response.json();
        total = Number(data.total) || 0;
        const items = data.items || [];
        termsNeedingAction += items.filter(
          (r) => r.pending_initiated_by === 'employer'
        ).length;
        if (items.length < perPage || page * perPage >= total) break;
        page += 1;
      }

      setPendingEmployersCount(total);
      setPendingEmployerTermsActionCount(termsNeedingAction);
    } catch {
      /* ignore */
    }
  };

  const fetchRelationships = async () => {
    setRelationshipsLoading(true);
    try {
      const statusFilter = activeTab === 'approved' ? 'active' : activeTab === 'pending' ? 'pending' : null;
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getRelationships));
      url.searchParams.append('page', '1');
      url.searchParams.append('per_page', '100');
      if (statusFilter) {
        url.searchParams.append('status_filter', statusFilter);
      }

      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });

      if (response.ok) {
        const data = await response.json();
        const employers = data.items || [];
        
        // Separate connected and pending (for backward compatibility with existing UI)
        const connected = employers.filter(emp => emp.status === 'active');
        const pending = employers.filter(emp => emp.status === 'pending');
        
        setConnectedEmployers(connected);
        setPendingRequests(pending);
        setEmployersTotal(data.total || 0);
        setEmployersTotalPages(data.total_pages || 0);

        if (statusFilter === 'active') {
          setApprovedEmployersCount(Number(data.total) || 0);
        } else if (statusFilter === 'pending') {
          setPendingEmployersCount(Number(data.total) || 0);
        }
        void fetchPendingEmployersSummary();
      }
    } catch (error) {
      // Silently fail - don't show error for relationships
    } finally {
      setRelationshipsLoading(false);
    }
  };

  const handleRequestConnection = async () => {
    if (!selectedEmployer) {
      setMessage({ type: 'error', text: 'Please select an employer first' });
      return;
    }

    // First, fetch and show contract terms
    setLoadingTerms(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.getEmployerContractTerms}/${selectedEmployer.employer_id}/contract-terms`),
        { method: 'GET' }
      );

      if (response.ok) {
        const data = await response.json();
        setContractTerms(data.contract_terms || '');
        setEmployerName(data.employer_company_name || data.employer_name || selectedEmployer.name);
        setContractModalFromFindEmployers(true);
        setAcceptInviteRelationshipId(null);
        setIsContractTermsModalOpen(true);
        setIsModalOpen(false); // Close the find employers modal
      } else {
        // If no contract terms or error, proceed with request
        setContractTerms('');
        setEmployerName(selectedEmployer.employer_company_name || selectedEmployer.name);
        setContractModalFromFindEmployers(true);
        setAcceptInviteRelationshipId(null);
        setIsContractTermsModalOpen(true);
        setIsModalOpen(false);
      }
    } catch (error) {
      // If error fetching terms, proceed anyway
      setContractTerms('');
      setEmployerName(selectedEmployer.employer_company_name || selectedEmployer.name);
      setContractModalFromFindEmployers(true);
      setAcceptInviteRelationshipId(null);
      setIsContractTermsModalOpen(true);
      setIsModalOpen(false);
    } finally {
      setLoadingTerms(false);
    }
  };

  const handleSendConnectionRequest = async () => {
    if (!termsAgreed && contractTerms) {
      setMessage({ type: 'error', text: 'Please agree to the contract terms to continue' });
      return;
    }

    if (!gdprConsentGiven) {
      setMessage({ type: 'error', text: 'Please confirm GDPR consent to share your data' });
      return;
    }

    if (!selectedEmployer) {
      setMessage({ type: 'error', text: 'Please select an employer first' });
      return;
    }

    setRequestLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (acceptInviteRelationshipId) {
        const response = await API_CONFIG.fetchWithAuth(
          API_CONFIG.getApiUrl(
            `${API_CONFIG.endpoints.acceptEmployerConnectionInvite}/${acceptInviteRelationshipId}/accept`
          ),
          {
            method: 'POST',
            body: JSON.stringify({ gdpr_consent_given: true }),
          }
        );
        if (response.ok) {
          const data = await response.json();
          setMessage({ type: 'success', text: data.message || 'You are now connected to this employer.' });
          setContractModalFromFindEmployers(false);
          setAcceptInviteRelationshipId(null);
          setSelectedEmployer(null);
          setSearchQuery('');
          setTermsAgreed(false);
          setGdprConsentGiven(false);
          setContractTerms('');
          setIsContractTermsModalOpen(false);
          await fetchRelationships();
          await fetchAllEmployers();
          setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } else {
          const errorData = await response.json();
          setMessage({
            type: 'error',
            text: errorData.detail || 'Failed to accept invitation',
          });
          setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        }
        return;
      }

      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.sendConnectionRequest),
        {
          method: 'POST',
          body: JSON.stringify({ 
            employer_id: selectedEmployer.employer_id,
            gdpr_consent_given: true
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message || 'Connection request sent successfully' });
        // Remove from list
        setAllEmployers(allEmployers.filter(emp => emp.employer_id !== selectedEmployer.employer_id));
        setFilteredEmployers(filteredEmployers.filter(emp => emp.employer_id !== selectedEmployer.employer_id));
        setSelectedEmployer(null);
        setSearchQuery('');
        setTermsAgreed(false);
        setGdprConsentGiven(false);
        setContractTerms('');
        // Close contract terms modal
        setIsContractTermsModalOpen(false);
        // Refresh relationships to show the new pending request
        await fetchRelationships();
        // Refresh available employers list
        await fetchAllEmployers();
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 2000);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to send connection request' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setRequestLoading(false);
    }
  };

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try {
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getPaymentRequests));
      url.searchParams.append('page', '1');
      url.searchParams.append('per_page', '100');
      url.searchParams.append('view_mode', 'subcontractor');

      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });

      if (response.ok) {
        const data = await response.json();
        // Store full payment data for detail view
        setPayments(data.items || []);
        setPaymentsTotal(data.total || 0);
        setPaymentsTotalPages(data.total_pages || 0);
      } else {
        console.error('Failed to fetch payments');
        setPayments([]);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleViewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setIsPaymentDetailModalOpen(true);
  };

  const handleDownloadPaymentPDF = async (paymentRequestId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.downloadPaymentPDF}/${paymentRequestId}/pdf`),
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Get filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `payment_statement_${paymentRequestId.substring(0, 8)}_${new Date().toISOString().split('T')[0]}.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setMessage({ type: 'success', text: 'PDF downloaded successfully' });
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to download PDF' });
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 5000);
      }
    } catch (error) {
      if (error.message !== 'Token expired') {
        setMessage({ type: 'error', text: 'Network error. Please try again.' });
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 5000);
      }
    }
  };

  const handleDownloadMonthlyStatementPDF = async (year, month) => {
    const key = `${year}-${month}`;
    setMonthlyStatementDownloading(key);
    try {
      const token = localStorage.getItem('access_token');
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.downloadMonthlyPaymentStatementsPDF));
      url.searchParams.append('year', year);
      url.searchParams.append('month', month);
      url.searchParams.append('view_mode', 'subcontractor');
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `monthly_payment_slips_${year}_${month}.pdf`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/i);
          if (match) filename = match[1];
        }
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
        setMessage({ type: 'success', text: 'Monthly statement PDF downloaded.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const err = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.detail || 'Failed to download PDF' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (e) {
      if (e.message !== 'Token expired') {
        setMessage({ type: 'error', text: 'Download failed.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } finally {
      setMonthlyStatementDownloading(null);
    }
  };

  const handleWithdrawPayment = (payment) => {
    setPaymentToWithdraw(payment);
    setIsWithdrawConfirmModalOpen(true);
  };

  const confirmWithdrawPayment = async () => {
    if (!paymentToWithdraw) return;

    setWithdrawLoading(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.withdrawPaymentRequest}/${paymentToWithdraw.payment_request_id}/withdraw`),
        { method: 'POST' }
      );

      if (response.ok) {
        // Refresh payments list first
        await fetchPayments();
        // Close modal
        setIsWithdrawConfirmModalOpen(false);
        setPaymentToWithdraw(null);
        // Show success message
        setMessage({ type: 'success', text: 'Payment request withdrawn successfully' });
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 5000);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to withdraw payment request' });
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 5000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
    } finally {
      setWithdrawLoading(false);
    }
  };

  const cancelWithdrawPayment = () => {
    setIsWithdrawConfirmModalOpen(false);
    setPaymentToWithdraw(null);
  };

  const handleOpenPaymentRequest = () => {
    setPaymentRequestStep(1);
    setSelectedPaymentEmployer(null);
    setPaymentType('');
    const org = currentUser?.organization || {};
    setPaymentVatRate(20);
    setReverseChargeSuggestion(!!org.subcontractor_reverse_charge_default);
    setMessage({ type: '', text: '' }); // Clear any previous messages
    setIsPaymentRequestModalOpen(true);
    void fetchActiveConnectedEmployersForPayments();
  };

  const handleClosePaymentRequest = () => {
    setIsPaymentRequestModalOpen(false);
    setRequestLoading(false); // Reset when modal closes (success or cancel)
    setPaymentRequestStep(1);
    setSelectedPaymentEmployer(null);
    setPaymentType('');
    setPaymentEmployerSearchQuery('');
    setShowMaterials(false);
    setIsPaymentEmployerDropdownOpen(false);
    setVerificationChecked(false);
    setMessage({ type: '', text: '' }); // Clear message when closing
    setJobRateFormData({
      workStartDate: '',
      description: '',
      jobCode: '',
      amount: ''
    });
    setByDayFormData({
      weekCommencing: '',
      days: {
        monday: { description: '', jobCode: '', amount: '' },
        tuesday: { description: '', jobCode: '', amount: '' },
        wednesday: { description: '', jobCode: '', amount: '' },
        thursday: { description: '', jobCode: '', amount: '' },
        friday: { description: '', jobCode: '', amount: '' },
        saturday: { description: '', jobCode: '', amount: '' },
        sunday: { description: '', jobCode: '', amount: '' }
      }
    });
    setMaterialsData([{ name: '', job_code: '', description: '', amount: '' }]);
  };

  const handleEmployerSelectForPayment = (employer) => {
    setSelectedPaymentEmployer(employer);
    setPaymentEmployerSearchQuery(employer.employer_name || employer.employer_email || '');
    setIsPaymentEmployerDropdownOpen(false);
    setPaymentRequestStep(2);
  };

  // Filter connected employers for payment dropdown
  const filteredPaymentEmployers = connectedEmployers.filter(employer => {
    const searchLower = paymentEmployerSearchQuery.toLowerCase();
    const name = (employer.employer_name || '').toLowerCase();
    const email = (employer.employer_email || '').toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower);
  });

  const handlePaymentTypeContinue = () => {
    if (!paymentType) {
      setMessage({ type: 'error', text: 'Please select a payment type' });
      return;
    }
    
    if (paymentType === 'job_rate') {
      // Advance to job rate form (step 3)
      setPaymentRequestStep(3);
    } else if (paymentType === 'by_day') {
      // Advance to by day form (step 4)
      setPaymentRequestStep(4);
    }
  };

  const handleJobRateFormChange = (field, value) => {
    setJobRateFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleJobRateFormSubmit = () => {
    // Validate required fields
    if (!jobRateFormData.workStartDate) {
      setMessage({ type: 'error', text: 'Please enter the Invoice Date' });
      return;
    }
    // Advance to review step
    setPaymentRequestStep(5);
  };

  const handleByDayFormChange = (day, field, value) => {
    setByDayFormData(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: {
          ...prev.days[day],
          [field]: value
        }
      }
    }));
  };

  const handleByDayWeekChange = (value) => {
    setByDayFormData(prev => ({
      ...prev,
      weekCommencing: value
    }));
  };

  // Handler to open date picker when clicking anywhere on the date input
  const handleDateInputClick = (inputRef) => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
      // Modern browsers support showPicker() method
      if (inputRef.current.showPicker) {
        try {
          inputRef.current.showPicker();
        } catch (error) {
          // Fallback to focus if showPicker fails
          inputRef.current.focus();
        }
      }
    }
  };

  const handleByDayFormSubmit = () => {
    // Validate required fields
    if (!byDayFormData.weekCommencing) {
      setMessage({ type: 'error', text: 'Please select the week commencing date' });
      return;
    }
    // Advance to review step
    setPaymentRequestStep(5);
  };

  const handleMaterialsChange = (index, field, value) => {
    const updated = [...materialsData];
    updated[index] = { ...updated[index], [field]: value };
    setMaterialsData(updated);
  };

  const handleAddMaterial = () => {
    setMaterialsData([...materialsData, { name: '', job_code: '', description: '', amount: '' }]);
  };

  const handleRemoveMaterial = (index) => {
    if (materialsData.length > 1) {
      const updated = materialsData.filter((_, i) => i !== index);
      setMaterialsData(updated);
    }
  };

  const handleShowMaterials = () => {
    setShowMaterials(true);
    // Scroll to materials section after a short delay to ensure it's rendered
    setTimeout(() => {
      // Scroll to the correct materials section based on current step
      if (paymentRequestStep === 3 && jobRateMaterialsSectionRef.current) {
        jobRateMaterialsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (paymentRequestStep === 4 && byDayMaterialsSectionRef.current) {
        byDayMaterialsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };


  // Check if bank details are complete
  const isBankDetailsComplete = useMemo(() => {
    if (!currentUser) return false;
    const accountHolderName = currentUser.account_holder_name || currentUser.bank_account_name;
    const sortCode = currentUser.sort_code || currentUser.bank_sort_code;
    const accountNumber = currentUser.account_number || currentUser.bank_account_number;
    return !!(accountHolderName && sortCode && accountNumber);
  }, [currentUser]);

  const handleFinalSubmit = async () => {
    if (!verificationChecked) {
      setMessage({ type: 'error', text: 'Please verify that all information is correct before submitting' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return;
    }

    if (!isBankDetailsComplete) {
      setMessage({ type: 'error', text: 'Please add your bank details (Account Holder Name, Sort Code, and Account Number) in your profile before submitting a payment request.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return;
    }

    setRequestLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      // Prepare materials data - filter out empty rows
      const filteredMaterials = materialsData
        .filter(m => m.name || m.description || m.amount || m.job_code)
        .map(m => ({
          name: m.name || '',
          job_code: (m.job_code || '').trim() || null,
          description: m.description || '',
          amount: m.amount ? parseFloat(m.amount) : 0
        }));

      // Prepare submission data
      const submissionData = {
        employer_id: selectedPaymentEmployer?.employer_id,
        payment_type: paymentType,
        ...(paymentType === 'job_rate' ? {
          work_start_date: jobRateFormData.workStartDate ? new Date(jobRateFormData.workStartDate).toISOString() : null,
          description: jobRateFormData.description || null,
          job_code: jobRateFormData.jobCode || null,
          amount: jobRateFormData.amount ? parseFloat(jobRateFormData.amount) : null
        } : {
          week_commencing: byDayFormData.weekCommencing ? new Date(byDayFormData.weekCommencing).toISOString() : null,
          days_data: byDayFormData.days || null
        }),
        materials_data: filteredMaterials.length > 0 ? filteredMaterials : null,
        ...(String(currentUser?.vat_registered || currentUser?.organization?.vat_registered || '').toLowerCase() === 'yes'
          ? {
              vat_rate: Number(paymentVatRate),
              subcontractor_reverse_charge_suggestion: reverseChargeSuggestion,
            }
          : {}),
      };

      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.createPaymentRequest),
        {
          method: 'POST',
          body: JSON.stringify(submissionData)
        }
      );

      if (response.ok) {
        // Success: keep button disabled (do NOT set requestLoading false); modal will close after delay
        const data = await response.json();
        
        // Build success message with CIS verification status if available
        let successMessage = 'Payment request submitted successfully! You and the employer will receive email notifications.';
        
        if (data.cis_verification_status) {
          let cisMessage = '';
          if (data.cis_verification_status === 'verified') {
            const cisStatus = data.hmrc_verification_status || 'UNVERIFIED';
            const cisRate = data.tax_rate ?? 0;
            cisMessage = ` ✅ CIS Verification: ${cisStatus} (${cisRate}% deduction rate)`;
          } else if (data.cis_verification_status === 'already_verified') {
            const cisStatus = data.hmrc_verification_status || 'UNVERIFIED';
            const cisRate = data.tax_rate ?? 0;
            cisMessage = ` ℹ️ CIS Status: ${cisStatus} (${cisRate}% deduction rate)`;
          } else if (data.cis_verification_status === 'failed' || data.cis_verification_status === 'error') {
            cisMessage = ` ⚠️ CIS Verification failed: ${data.cis_verification_error || 'Unable to verify CIS status'}. Payment will proceed with 30% deduction rate.`;
          } else if (data.cis_verification_status === 'skipped') {
            cisMessage = ` ⚠️ CIS Verification skipped: ${data.cis_verification_error || 'Missing required information'}. Payment will proceed with 30% deduction rate.`;
          } else if (data.cis_verification_status === 'employer_not_connected') {
            cisMessage = ` ℹ️ CIS Verification not available: Employer HMRC account not connected.`;
          }
          successMessage += cisMessage;
        }
        
        setMessage({ type: 'success', text: successMessage });
        
        // Close modal after a longer delay to show success message
        setTimeout(() => {
          handleClosePaymentRequest();
          // Clear message after modal closes
          setMessage({ type: '', text: '' });
          // Refresh payments list
          fetchPayments();
        }, 6000); // Increased delay to show CIS verification message
      } else {
        setRequestLoading(false); // Error: re-enable button so user can retry
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to submit payment request' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      setRequestLoading(false); // Error: re-enable button so user can retry
      if (error.message !== 'Token expired') {
        setMessage({ type: 'error', text: 'Network error. Please try again.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    }
  };

  // Emergency-tax guidance: infer from profile completeness only (legacy user-level cis_rate removed).
  const isOnEmergencyTaxRate = useMemo(() => {
    if (!currentUser) return false;
    const org = currentUser.organization || {};
    const utrStr = (v) => (typeof v === 'string' ? v.trim() : v != null ? String(v).trim() : '');
    const hasUTR = Boolean(
      utrStr(currentUser.utr) ||
        utrStr(org.utr) ||
        utrStr(currentUser.unique_tax_reference) ||
        utrStr(org.unique_tax_reference)
    );
    const businessType = currentUser.business_type || org.business_type || '';
    const isBusinessType = businessType === 'Business (Ltd co or Partnership)';
    const niRaw = currentUser.ni_number ?? org.ni_number;
    const hasNINumber = Boolean(niRaw && String(niRaw).trim() !== '');
    if (isBusinessType) return !hasUTR;
    return !hasUTR || !hasNINumber;
  }, [currentUser]);

  useEffect(() => {
    const items = [
      {
        id: 'payments',
        label: 'Payments',
        active: activeMenu === 'payments',
        onActivate: () => setActiveMenu('payments'),
      },
      {
        id: 'employers',
        label: 'Employers',
        active: activeMenu === 'employers',
        badge: pendingEmployerTermsActionCount > 0 ? pendingEmployerTermsActionCount : undefined,
        onActivate: () => {
          setActiveMenu('employers');
          setActiveTab('approved');
          setEmployersPage(1);
        },
      },
    ];
    setDashboardNav(items);
    return () => clearDashboardNav();
  }, [activeMenu, pendingEmployerTermsActionCount, setDashboardNav, clearDashboardNav]);

  return (
    <div className="subcontractor-dashboard">
      {/* UTR / tax details modal (from Emergency Tax Rate banner) */}
      {showUtrModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => !utrSaveLoading && setShowUtrModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h2>Add UTR</h2>
              <button type="button" className="modal-close" onClick={() => !utrSaveLoading && setShowUtrModal(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', fontSize: '14px', color: 'rgba(255,255,255,0.85)' }}>
                Enter your Unique Tax Reference (UTR). This helps reduce your deduction rate from 30%.
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>UTR (10 digits)</label>
                <input
                  type="text"
                  value={utrModalUtr}
                  onChange={(e) => setUtrModalUtr(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="e.g. 1234567890"
                  maxLength={10}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.08)',
                    color: '#fff',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => !utrSaveLoading && setShowUtrModal(false)}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    background: 'transparent',
                    color: '#fff',
                    cursor: utrSaveLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={utrSaveLoading || utrModalUtr.replace(/\D/g, '').length !== 10}
                  onClick={async () => {
                    const digits = utrModalUtr.replace(/\D/g, '');
                    if (digits.length !== 10) {
                      if (window.showToast) window.showToast('UTR must be exactly 10 digits', 'error');
                      return;
                    }
                    setUtrSaveLoading(true);
                    try {
                      const body = {
                        utr: digits,
                        unique_tax_reference: digits
                      };
                      const response = await API_CONFIG.fetchWithAuth(
                        API_CONFIG.getApiUrl('/unified-auth/profile'),
                        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
                      );
                      if (response.ok) {
                        const updatedUser = await response.json();
                        localStorage.setItem('user', JSON.stringify(updatedUser));
                        setLocalUser(updatedUser);
                        window.dispatchEvent(new Event('user-updated'));
                        setShowUtrModal(false);
                        if (window.showToast) window.showToast('UTR and tax details saved. Your employer may need to verify your CIS status again.', 'success');
                      } else {
                        const err = await response.json();
                        const msg = err.detail || (Array.isArray(err.detail) ? err.detail.map(d => d.msg).join(', ') : 'Failed to save');
                        if (window.showToast) window.showToast(msg, 'error');
                      }
                    } catch (e) {
                      if (window.showToast) window.showToast('Network error. Please try again.', 'error');
                    } finally {
                      setUtrSaveLoading(false);
                    }
                  }}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '6px',
                    border: 'none',
                    background: utrSaveLoading ? 'rgba(91, 192, 222, 0.5)' : '#57bedd',
                    color: '#fff',
                    cursor: utrSaveLoading ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  {utrSaveLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* <div style={{ marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>Subcontractor Dashboard</h1>
      </div> */}
      <div className="dashboard-content">
        {(() => {
          const dashboardSidebar = (
        <div className={`dashboard-sidebar${mobileBottomNav ? ' dashboard-sidebar--mobile-fixed' : ''}`}>
          <div className="sidebar-menu">
            <div 
              className={`sidebar-menu-item ${activeMenu === 'payments' ? 'active' : ''}`}
              onClick={() => setActiveMenu('payments')}
            >
              <span className="menu-icon">💰</span>
              <span className="menu-text">Payments</span>
            </div>
            <div 
              className={`sidebar-menu-item ${activeMenu === 'employers' ? 'active' : ''}`}
              onClick={() => {
                setActiveMenu('employers');
                setActiveTab('approved');
                setEmployersPage(1);
              }}
            >
              <span className="menu-icon">👥</span>
              <span className="menu-text">Employers</span>
              {pendingEmployerTermsActionCount > 0 && (
                <span
                  className="sidebar-menu-badge"
                  title="Employer invitations need your review"
                >
                  {pendingEmployerTermsActionCount}
                </span>
              )}
            </div>
          </div>
        </div>
          );
          return (
            <>
              {!mobileBottomNav && dashboardSidebar}
              {mobileBottomNav && ReactDOM.createPortal(dashboardSidebar, document.body)}
            </>
          );
        })()}

        <div className="dashboard-main">
          {isOnEmergencyTaxRate && (
            <div
              className="subcontractor-emergency-tax-banner"
              role="alert"
              style={{
                backgroundColor: '#fff3cd',
                border: '2px solid #ffc107',
                borderRadius: '8px',
                padding: '16px 20px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div style={{ fontSize: '24px', flexShrink: 0 }} aria-hidden="true">
                ⚠️
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#856404',
                    marginBottom: '4px',
                  }}
                >
                  Emergency Tax Rate Active
                </div>
                <div style={{ fontSize: '14px', color: '#856404', lineHeight: '1.5' }}>
                  You are currently on a 30% emergency tax rate because your UTR or NI Number is missing.
                  {' '}
                  <button
                    type="button"
                    onClick={() => {
                      const o = currentUser?.organization || {};
                      setUtrModalUtr(
                        (
                          currentUser?.utr ||
                          o.utr ||
                          currentUser?.unique_tax_reference ||
                          o.unique_tax_reference ||
                          ''
                        ).toString().trim()
                      );
                      setShowUtrModal(true);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: '#856404',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  >
                    Add UTR
                  </button>
                  {' '}
                  to update your profile and resolve this.
                </div>
              </div>
            </div>
          )}

          {pendingEmployerTermsActionCount > 0 && (
            <div
              className="pending-terms-notification"
              role="status"
              aria-live="polite"
            >
              <div className="pending-terms-notification-icon" aria-hidden="true">
                ⚠️
              </div>
              <div className="pending-terms-notification-body">
                {/* <strong>Action required: review employer invitations</strong> */}
                <p style={{ marginBottom: '0px' }}>
                  You have{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {pendingEmployerTermsActionCount}{' '}
                    {pendingEmployerTermsActionCount === 1
                      ? 'employer'
                      : 'employers'}
                  </span>{' '}
                  waiting for you to review and accept their contract terms before you can
                  connect.
                </p>
              </div>
              <button
                type="button"
                className="pending-terms-notification-cta"
                onClick={() => {
                  setActiveMenu('employers');
                  setEmployersPage(1);
                  setActiveTab('pending');
                }}
              >
                Review now
              </button>
            </div>
          )}

          {activeMenu === 'employers' && (
            <>
              {/* My Employers Section with Tabs */}
              <div className="relationships-section">
                <div className="section-header section-header--actions-only dashboard-tab-actions--mobile">
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => setIsModalOpen(true)}
                  >
                    + Find Employers
                  </button>
                </div>
        {/* Tabs (+ Find Employers on web, right) */}
        <div className="tabs-container tabs-container--with-inline-actions">
          <div className="tabs-container__tabs">
          <button
            type="button"
            className={`tab-button ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => {
              setEmployersPage(1);
              setActiveTab('approved');
            }}
          >
            Approved ({approvedEmployersCount})
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => {
              setEmployersPage(1);
              setActiveTab('pending');
            }}
          >
            Pending ({pendingEmployersCount})
          </button>
          </div>
          <div className="tabs-container__actions dashboard-tab-actions--desktop">
            <button
              type="button"
              className="action-button"
              onClick={() => setIsModalOpen(true)}
            >
              + Find Employers
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {relationshipsLoading ? (
          <div className="loading-state">Loading relationships...</div>
        ) : (
          <>
            {activeTab === 'approved' && (
              <>
                {connectedEmployers.length > 0 ? (
                  <div className="table-container">
                    <table className="relationships-table">
                      <thead>
                        <tr>
                          <th>Company Name</th>
                          <th>Connected Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {connectedEmployers.map((employer) => (
                          <tr key={employer.employer_id} className="table-row connected">
                            <td>
                              <button
                                type="button"
                                onClick={() => handleOpenEmployerInfo(employer)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#57bedd',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: 0,
                                  fontSize: 'inherit',
                                  fontFamily: 'inherit',
                                  textAlign: 'left',
                                }}
                              >
                                {employer.employer_name || 'N/A'}
                              </button>
                            </td>
                            <td>
                              {employer.connected_at 
                                ? new Date(employer.connected_at).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td>
                              <span className="status-badge status-active">Connected</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>You don't have any connected employers yet.</p>
                    <p>Use the search above to find and request connections with employers.</p>
                  </div>
                )}
                {/* Pagination */}
                {false && connectedEmployers.length > 0 && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      Showing {((employersPage - 1) * employersPerPage) + 1} to {Math.min(employersPage * employersPerPage, employersTotal)} of {employersTotal} results
                    </div>
                    <div className="pagination-controls">
                      <select
                        value={employersPerPage}
                        onChange={(e) => {
                          setEmployersPerPage(Number(e.target.value));
                          setEmployersPage(1);
                        }}
                        className="pagination-select"
                      >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                      </select>
                      <button
                        onClick={() => setEmployersPage(prev => Math.max(1, prev - 1))}
                        disabled={employersPage === 1}
                        className="pagination-button"
                      >
                        Previous
                      </button>
                      <span className="pagination-page-info">
                        Page {employersPage} of {employersTotalPages || 1}
                      </span>
                      <button
                        onClick={() => setEmployersPage(prev => Math.min(employersTotalPages, prev + 1))}
                        disabled={employersPage >= employersTotalPages}
                        className="pagination-button"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'pending' && (
              <>
                {pendingRequests.length > 0 ? (
                  <div className="table-container">
                    <table className="relationships-table">
                      <thead>
                        <tr>
                          <th>Company Name</th>
                          <th>Requested Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingRequests.map((employer) => (
                          <tr key={employer.relationship_id || employer.employer_id} className="table-row pending">
                            <td>
                              <button
                                type="button"
                                onClick={() => handleOpenEmployerInfo(employer)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#57bedd',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: 0,
                                  fontSize: 'inherit',
                                  fontFamily: 'inherit',
                                  textAlign: 'left',
                                }}
                              >
                                {employer.employer_name || 'N/A'}
                              </button>
                            </td>
                            <td>
                              {employer.connected_at 
                                ? new Date(employer.connected_at).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td>
                              {employer.pending_initiated_by === 'employer' ? (
                                <span className="status-badge status-pending">Action required — review terms</span>
                              ) : (
                                <span className="status-badge status-pending">Awaiting employer approval</span>
                              )}
                            </td>
                            <td>
                              {employer.pending_initiated_by === 'employer' && (
                                <button
                                  type="button"
                                  className="action-button"
                                  disabled={loadingTerms}
                                  onClick={() => handleStartAcceptEmployerInvite(employer)}
                                  style={{ fontSize: '13px', padding: '8px 14px' }}
                                >
                                  {loadingTerms ? 'Loading...' : 'Review Terms & Accept'}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>You don't have any pending connection requests.</p>
                  </div>
                )}
                {/* Pagination */}
                {false && pendingRequests.length > 0 && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      Showing {((employersPage - 1) * employersPerPage) + 1} to {Math.min(employersPage * employersPerPage, employersTotal)} of {employersTotal} results
                    </div>
                    <div className="pagination-controls">
                      <select
                        value={employersPerPage}
                        onChange={(e) => {
                          setEmployersPerPage(Number(e.target.value));
                          setEmployersPage(1);
                        }}
                        className="pagination-select"
                      >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                      </select>
                      <button
                        onClick={() => setEmployersPage(prev => Math.max(1, prev - 1))}
                        disabled={employersPage === 1}
                        className="pagination-button"
                      >
                        Previous
                      </button>
                      <span className="pagination-page-info">
                        Page {employersPage} of {employersTotalPages || 1}
                      </span>
                      <button
                        onClick={() => setEmployersPage(prev => Math.min(employersTotalPages, prev + 1))}
                        disabled={employersPage >= employersTotalPages}
                        className="pagination-button"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Find Employers Modal - no backdrop click to close; user may switch windows */}
      {isModalOpen && ReactDOM.createPortal(
        <div className="modal-overlay">
          <div className="modal-content find-employer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Find Employers</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Select an employer from the dropdown to request a connection</p>
              
              <div className="dropdown-container" ref={dropdownRef}>
                <div className="dropdown-input-wrapper">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search or select an employer..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => setIsDropdownOpen(true)}
                    className="dropdown-input"
                  />
                  <span className="dropdown-arrow">▼</span>
                </div>

                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    {loading ? (
                      <div className="dropdown-item">Loading...</div>
                    ) : filteredEmployers.length === 0 ? (
                      <div className="dropdown-item no-results">No employers found</div>
                    ) : (
                      filteredEmployers.map((employer) => (
                        <div
                          key={employer.employer_id}
                          className={`dropdown-item ${selectedEmployer?.employer_id === employer.employer_id ? 'selected' : ''}`}
                          onClick={() => handleSelectEmployer(employer)}
                        >
                          <div className="employer-dropdown-info">
                            <strong>{employer.name}</strong>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {selectedEmployer && (
                <div className="selected-employer-card">
                  <div className="employer-info">
                    <h4>{selectedEmployer.name}</h4>
                  </div>
                  <button
                    onClick={handleRequestConnection}
                    disabled={requestLoading || loadingTerms}
                    className="request-button"
                  >
                    {loadingTerms ? 'Loading...' : requestLoading ? 'Sending...' : 'Request Connection'}
                  </button>
                </div>
              )}

              {message.text && (
                <div className={`message ${message.type}`}>
                  {message.text}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Contract Terms Modal - no backdrop click to close; user may switch windows to read terms */}
      {isContractTermsModalOpen && ReactDOM.createPortal(
        <div className="modal-overlay">
          <div className="modal-content contract-terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {acceptInviteRelationshipId
                  ? `Accept invitation — ${employerName}`
                  : `Before Connecting — ${employerName}`}
              </h2>
              <button className="modal-close" onClick={() => {
                setIsContractTermsModalOpen(false);
                setTermsAgreed(false);
                setGdprConsentGiven(false);
                setAcceptInviteRelationshipId(null);
                if (contractModalFromFindEmployers) setIsModalOpen(true);
                setContractModalFromFindEmployers(false);
              }}>×</button>
            </div>
            <div className="modal-body">
              {/* GDPR Consent Section */}
              <div className="contract-terms-content" style={{ marginBottom: '20px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: 'bold' }}>
                  Data Sharing Consent
                </h3>
                <p style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6' }}>
                  By accepting this invitation, you authorise PayCIS to share your registration and tax information 
                  (name, business details, UTR, NI, verification status, payment information) with the contractor 
                  listed below for the purpose of verifying your CIS status and processing payments.
                </p>
                <p style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6' }}>
                  This data will be accessible only to that contractor's authorised users and will not be visible to others. 
                  You can remove the connection at any time from your account.
                </p>
                <div className="terms-agreement">
                  <label className="terms-checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={gdprConsentGiven}
                      onChange={(e) => setGdprConsentGiven(e.target.checked)}
                      className="terms-checkbox"
                      style={{ marginTop: '4px', marginRight: '8px', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
                      I confirm that I authorise PayCIS to share my registration and tax information with {employerName} 
                      &nbsp;for the purpose of verifying my CIS status and processing payments.
                    </span>
                  </label>
                </div>
              </div>

              {contractTerms ? (
                <>
                  <p>Please read the following contract terms before requesting a connection:</p>
                  <div className="contract-terms-content">
                    <pre>{contractTerms}</pre>
                  </div>
                  <div className="terms-agreement">
                    <label className="terms-checkbox-label">
                      <input
                        type="checkbox"
                        checked={termsAgreed}
                        onChange={(e) => setTermsAgreed(e.target.checked)}
                        className="terms-checkbox"
                      />
                      <span>I have read and agree to the contract terms</span>
                    </label>
                  </div>
                  <div className="modal-actions">
                    <button
                      onClick={() => {
                        setIsContractTermsModalOpen(false);
                        setTermsAgreed(false);
                        setGdprConsentGiven(false);
                        setAcceptInviteRelationshipId(null);
                        if (contractModalFromFindEmployers) setIsModalOpen(true);
                        setContractModalFromFindEmployers(false);
                      }}
                      className="cancel-button"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendConnectionRequest}
                      disabled={!termsAgreed || !gdprConsentGiven || requestLoading}
                      className="request-button"
                    >
                      {requestLoading
                        ? 'Sending...'
                        : acceptInviteRelationshipId
                          ? 'Accept & connect'
                          : 'Send Connection Request'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="modal-actions">
                    <button
                      onClick={() => {
                        setIsContractTermsModalOpen(false);
                        setTermsAgreed(false);
                        setGdprConsentGiven(false);
                        setAcceptInviteRelationshipId(null);
                        if (contractModalFromFindEmployers) setIsModalOpen(true);
                        setContractModalFromFindEmployers(false);
                      }}
                      className="cancel-button"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendConnectionRequest}
                      disabled={!gdprConsentGiven || requestLoading}
                      className="request-button"
                    >
                      {requestLoading
                        ? 'Sending...'
                        : acceptInviteRelationshipId
                          ? 'Accept & connect'
                          : 'Send Connection Request'}
                    </button>
                  </div>
                </>
              )}
              {message.text && (
                <div className={`message ${message.type}`}>
                  {message.text}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {employerInfoModalOpen && ReactDOM.createPortal(
        <div className="modal-overlay">
          <div className="modal-content contract-terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Employer details & contract</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setEmployerInfoModalOpen(false);
                  setEmployerInfoData(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {employerInfoLoading ? (
                <div className="loading-state">Loading...</div>
              ) : employerInfoData && !employerInfoData.error ? (
                <>
                  <h3 style={{ marginTop: 0 }}>
                    {employerInfoData.employer_company_name || employerInfoData.employer_name || 'Employer'}
                  </h3>
                  <div className="payment-detail-section">
                    <h4>Contact</h4>
                    {employerInfoData.contact_email && (
                      <p><strong>Email:</strong> {employerInfoData.contact_email}</p>
                    )}
                    {employerInfoData.contact_phone && (
                      <p><strong>Phone:</strong> {employerInfoData.contact_phone}</p>
                    )}
                    {employerInfoData.contact_address && (
                      <p><strong>Address:</strong> {employerInfoData.contact_address}</p>
                    )}
                    {!employerInfoData.contact_email && !employerInfoData.contact_phone && !employerInfoData.contact_address && (
                      <p style={{ color: 'rgba(255,255,255,0.7)' }}>No contact details on file.</p>
                    )}
                  </div>
                  <div className="payment-detail-section">
                    <h4>Contract terms</h4>
                    {employerInfoData.contract_terms ? (
                      <div className="contract-terms-content">
                        <pre style={{ whiteSpace: 'pre-wrap' }}>{employerInfoData.contract_terms}</pre>
                      </div>
                    ) : (
                      <p>No contract terms on file for this employer.</p>
                    )}
                  </div>
                </>
              ) : (
                <p>Could not load employer details.</p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
            </>
          )}

          {activeMenu === 'payments' && (
            <div className="payments-section">
              <div className="section-header section-header--actions-only dashboard-tab-actions--mobile">
                <button
                  type="button"
                  className="action-button"
                  onClick={handleOpenPaymentRequest}
                  disabled={!isBankDetailsComplete || paymentSubTab !== 'list'}
                  style={{ opacity: paymentSubTab !== 'list' ? 0.7 : 1 }}
                >
                  + Request Payment
                </button>
              </div>
              <div
                className="tabs-container tabs-container--with-inline-actions"
                style={{ marginBottom: '16px' }}
              >
                <div className="tabs-container__tabs">
                <button
                  type="button"
                  className={`tab-button ${paymentSubTab === 'list' ? 'active' : ''}`}
                  onClick={() => setPaymentSubTab('list')}
                >
                  Payment requests
                </button>
                <button
                  type="button"
                  className={`tab-button ${paymentSubTab === 'monthly' ? 'active' : ''}`}
                  onClick={() => setPaymentSubTab('monthly')}
                >
                  Monthly statements
                </button>
                </div>
                <div className="tabs-container__actions dashboard-tab-actions--desktop">
                  <button
                    type="button"
                    className="action-button"
                    onClick={handleOpenPaymentRequest}
                    disabled={!isBankDetailsComplete || paymentSubTab !== 'list'}
                    style={{ opacity: paymentSubTab !== 'list' ? 0.7 : 1 }}
                  >
                    + Request Payment
                  </button>
                </div>
              </div>

              {paymentSubTab === 'monthly' && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>Year:</span>
                      <select
                        value={monthlyStatementsYear}
                        onChange={(e) => setMonthlyStatementsYear(Number(e.target.value))}
                        style={{ padding: '8px 12px', borderRadius: '4px', minWidth: '100px' }}
                      >
                        {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2, new Date().getFullYear() - 3].map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {monthlyStatementsLoading ? (
                    <div className="loading-state">Loading monthly statements...</div>
                  ) : monthlyStatementsItems.length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.7)' }}>No paid payments for the selected year.</p>
                  ) : (
                    <div className="table-container">
                      <table className="relationships-table">
                        <thead>
                          <tr>
                            <th>Period</th>
                            <th>Payments</th>
                            <th>Total net</th>
                            <th>Download</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyStatementsItems.map((row) => {
                            const key = `${row.year}-${row.month}`;
                            const downloading = monthlyStatementDownloading === key;
                            return (
                              <tr key={key} className="table-row">
                                <td>{row.label}</td>
                                <td>{row.payment_count}</td>
                                <td>£{typeof row.total_net === 'number' ? row.total_net.toFixed(2) : row.total_net}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="view-button"
                                    disabled={downloading}
                                    onClick={() => handleDownloadMonthlyStatementPDF(row.year, row.month)}
                                    title={downloading ? 'Downloading...' : 'Download PDF'}
                                    style={{
                                      background: 'transparent',
                                      border: '1px solid rgba(40, 167, 69, 0.5)',
                                      color: downloading ? '#6c757d' : '#28a745',
                                      padding: '6px 10px',
                                      cursor: downloading ? 'not-allowed' : 'pointer',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minWidth: '32px',
                                      height: '32px',
                                      opacity: downloading ? 0.7 : 1
                                    }}
                                  >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                      <polyline points="7 10 12 15 17 10"></polyline>
                                      <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {paymentSubTab === 'list' && !isBankDetailsComplete && (
                <div className="message error" style={{ 
                  marginBottom: '20px', 
                  padding: '15px',
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  borderRadius: '6px',
                  color: '#ffcdd2'
                }}>
                  <strong>Bank Details Required:</strong> Please add your bank details (Account Holder Name, Sort Code, and Account Number) in your profile before submitting a payment request. </div>
              )}

              {paymentSubTab === 'list' && message.text && (
                <div className={`message ${message.type}`} style={{ marginBottom: '20px' }}>
                  {message.text}
                </div>
              )}

              {paymentSubTab === 'list' && (paymentsLoading ? (
                <div className="loading-state">Loading payments...</div>
              ) : payments.length > 0 ? (
                <div className="table-container">
                  <table className="relationships-table">
                    <thead>
                      <tr>
                        <th>Employer</th>
                        <th>Payment Type</th>
                        <th>Net Payable</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.payment_request_id} className="table-row">
                          <td>
                            <div>
                              <div>{payment.employer_name || 'N/A'}</div>
                              <div className="relationship-date">{payment.employer_email || ''}</div>
                            </div>
                          </td>
                          <td>{payment.payment_type === 'job_rate' ? 'Job Rate' : 'Day Rate'}</td>
                          <td>{formatPaymentNetPayable(payment)}</td>
                          <td>
                            {payment.submitted_at 
                              ? new Date(payment.submitted_at).toLocaleDateString()
                              : 'N/A'}
                          </td>
                          <td>
                            <span className={`status-badge status-${payment.status || 'pending'}`}>
                              {payment.status === 'approved' ? 'To be paid' : payment.status ? payment.status.charAt(0).toUpperCase() + payment.status.slice(1) : 'Pending'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <button
                                className="view-button"
                                onClick={() => handleViewPaymentDetails(payment)}
                                title="View Details"
                                style={{
                                  background: 'transparent',
                                  border: '1px solid rgba(91, 192, 222, 0.5)',
                                  color: '#57bedd',
                                  padding: '6px 10px',
                                  cursor: 'pointer',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '32px',
                                  height: '32px'
                                }}
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                  <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                              </button>
                              {payment.status === 'pending' && (
                                <button
                                  className="view-button"
                                  onClick={() => handleWithdrawPayment(payment)}
                                  title="Withdraw"
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid rgba(244, 67, 54, 0.5)',
                                    color: '#f44336',
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '32px',
                                    height: '32px'
                                  }}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                  </svg>
                                </button>
                              )}
                              {(payment.status === 'approved' || payment.status === 'paid') && (
                                <button
                                  className="view-button"
                                  onClick={() => handleDownloadPaymentPDF(payment.payment_request_id)}
                                  title="Download Invoice PDF"
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid rgba(40, 167, 69, 0.5)',
                                    color: '#28a745',
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '32px',
                                    height: '32px'
                                  }}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>You don't have any payment requests yet.</p>
                  <p>Click "Request Payment" to create a new payment request.</p>
                </div>
              ))}
              {/* Pagination */}
              {false && paymentSubTab === 'list' && payments.length > 0 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {((paymentsPage - 1) * paymentsPerPage) + 1} to {Math.min(paymentsPage * paymentsPerPage, paymentsTotal)} of {paymentsTotal} results
                  </div>
                  <div className="pagination-controls">
                    <select
                      value={paymentsPerPage}
                      onChange={(e) => {
                        setPaymentsPerPage(Number(e.target.value));
                        setPaymentsPage(1);
                      }}
                      className="pagination-select"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <button
                      onClick={() => setPaymentsPage(prev => Math.max(1, prev - 1))}
                      disabled={paymentsPage === 1}
                      className="pagination-button"
                    >
                      Previous
                    </button>
                    <span className="pagination-page-info">
                      Page {paymentsPage} of {paymentsTotalPages || 1}
                    </span>
                    <button
                      onClick={() => setPaymentsPage(prev => Math.min(paymentsTotalPages, prev + 1))}
                      disabled={paymentsPage >= paymentsTotalPages}
                      className="pagination-button"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Payment Request Modal - no backdrop click to close; user may switch windows to look up bank details */}
      {isPaymentRequestModalOpen && ReactDOM.createPortal(
        <div className="modal-overlay">
          <div className="modal-content find-employer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {paymentRequestStep === 1 ? 'Select Employer' : 
                 paymentRequestStep === 2 ? 'Payment Type' : 
                 paymentRequestStep === 3 ? 'Payment Application' :
                 paymentRequestStep === 4 ? 'Time Sheet' :
                 'Review Payment Request'}
              </h2>
              <button className="modal-close" onClick={handleClosePaymentRequest}>×</button>
            </div>
            <div className="modal-body" style={{ paddingTop: '0px' }}>
              {!isBankDetailsComplete && (
                <div className="message error" style={{ 
                  marginBottom: '20px', 
                  padding: '15px',
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  borderRadius: '6px',
                  color: '#ffcdd2'
                }}>
                  <strong>Bank Details Required:</strong> Please add your bank details (Account Holder Name, Sort Code, and Account Number) in your profile before submitting a payment request. <a href="/subcontractor/profile" target="_blank" style={{ color: '#57bedd', textDecoration: 'underline' }}>Go to Profile</a>
                </div>
              )}
              {paymentRequestStep === 1 ? (
                <div className="employer-selection-step">
                  <p className="step-instruction">Please select the employer for this payment request.</p>
                  {connectedEmployers.length === 0 ? (
                    <div className="empty-state">
                      <p>You don't have any connected employers yet.</p>
                      <p>Please connect with an employer first before requesting payment.</p>
                    </div>
                  ) : (
                    <div className="dropdown-container" ref={paymentEmployerDropdownRef}>
                      <label>Select Employer:</label>
                      <div className="dropdown-input-wrapper" style={{ marginTop: '10px' }}>
                        <input
                          ref={paymentEmployerInputRef}
                          type="text"
                          placeholder="Search or select an employer..."
                          value={paymentEmployerSearchQuery}
                          onChange={(e) => {
                            setPaymentEmployerSearchQuery(e.target.value);
                            setIsPaymentEmployerDropdownOpen(true);
                          }}
                          onFocus={() => setIsPaymentEmployerDropdownOpen(true)}
                          className="dropdown-input"
                        />
                        <span className="dropdown-arrow">▼</span>
                      </div>

                      {isPaymentEmployerDropdownOpen && (
                        <div className="dropdown-menu">
                          {filteredPaymentEmployers.length === 0 ? (
                            <div className="dropdown-item no-results">No employers found</div>
                          ) : (
                            filteredPaymentEmployers.map((employer) => (
                              <div
                                key={employer.employer_id}
                                className={`dropdown-item ${selectedPaymentEmployer?.employer_id === employer.employer_id ? 'selected' : ''}`}
                                onClick={() => handleEmployerSelectForPayment(employer)}
                              >
                                <div className="employer-dropdown-info">
                                  <strong>{employer.employer_name || 'N/A'}</strong>
                                  <span>{employer.employer_email}</span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : paymentRequestStep === 2 ? (
                <div className="payment-type-step">
                  <div className="payment-type-container">
                    {/* <h3 className="payment-type-title">PAYMENT TYPE</h3> */}
                    {/* <p className="payment-instruction">Please select how you wish to be paid.</p> */}
                    <div className="payment-explanations" style={{ marginBottom: '0px' }}>
                      <p>
                        If you get paid by the day, i.e. a set amount for each day that you work, then please select 'I AM APPLYING TO BE PAID BY THE DAY'
                      </p>
                      <p>
                        If you have agreed an amount for the work that you have carried out then please select 'I AM APPLYING FOR A JOB RATE PAYMENT'.
                      </p>
                    </div>
                    <div className="payment-notes" style={{ marginBottom: '5px' }}>
                      <p><strong>N.b.</strong> If you are applying for both then it is easier to make two separate applications.</p>
                      <p>If you have supplied any materials you can add these using the form.</p>
                    </div>
                    <div className="payment-type-dropdown-container">
                      <h3 className="payment-type-select-title">SELECT PAYMENT TYPE:</h3>
                      <select
                        className="payment-type-select"
                        value={paymentType}
                        onChange={(e) => {
                          setPaymentType(e.target.value);
                          setShowMaterials(false); // Reset materials visibility when payment type changes
                        }}
                      >
                        <option value="">-- Select payment type --</option>
                        <option value="by_day">I AM APPLYING TO BE PAID BY THE DAY</option>
                        <option value="job_rate">I AM APPLYING FOR A JOB RATE PAYMENT</option>
                      </select>
                    </div>
                  </div>
                </div>
              ) : paymentRequestStep === 3 ? (
                <div className="job-rate-form-step">
                  <div className="job-rate-form-container">
                    <h3 className="job-rate-form-title">Contract works</h3>
                    
                    <div className="job-rate-form-section">
                      <div className="job-rate-instructions">
                        <p>Please fill in this form to apply for a job rate payment.</p>
                        <p>Add the cost of labour on the right hand side. If you have supplied any materials, you can add them below. Tax will not be deducted for materials.</p>
                        <p>Note that if you have worked on more than one project you will need to make separate applications for each job.</p>
                      </div>

                      <div className="form-field-group">
                        <label 
                          className="form-label required"
                          onClick={() => handleDateInputClick(workStartDateRef)}
                          style={{ cursor: 'pointer' }}
                        >
                          Invoice Date
                        </label>
                        <input
                          ref={workStartDateRef}
                          type="date"
                          className="form-input"
                          value={jobRateFormData.workStartDate}
                          onChange={(e) => handleJobRateFormChange('workStartDate', e.target.value)}
                          onClick={() => handleDateInputClick(workStartDateRef)}
                          onFocus={() => handleDateInputClick(workStartDateRef)}
                          required
                        />
                      </div>

                      <div className="form-field-group">
                        <label className="form-label">
                          Description of works carried out
                        </label>
                        <textarea
                          className="form-textarea"
                          rows="6"
                          value={jobRateFormData.description}
                          onChange={(e) => handleJobRateFormChange('description', e.target.value)}
                          placeholder="Enter description of works..."
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-field-group">
                          <label className="form-label">
                            Job Code
                          </label>
                          <input
                            type="text"
                            className="form-input"
                            value={jobRateFormData.jobCode}
                            onChange={(e) => handleJobRateFormChange('jobCode', e.target.value)}
                            placeholder="Enter job code"
                          />
                        </div>

                        <div className="form-field-group">
                          <label className="form-label">
                            Amount
                          </label>
                          <div className="materials-amount-wrapper">
                            <span className="materials-amount-prefix">£</span>
                          <input
                            type="number"
                            style={{ padding: '10px 30px' }}
                            className="materials-input materials-amount-input"
                            value={jobRateFormData.amount}
                            onChange={(e) => handleJobRateFormChange('amount', e.target.value)}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Materials Section - Inline */}
                    {showMaterials && (
                      <div ref={jobRateMaterialsSectionRef} style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <div>
                          <div style={{ marginBottom: '15px' }}>
                            <h4 style={{ color: 'white', marginBottom: '10px', fontSize: '16px' }}>Materials</h4>
                            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', marginBottom: '15px' }}>
                              Add any materials you have supplied. Tax will not be deducted for materials.
                            </p>
                          </div>
                          <div className="materials-table">
                            <div className="materials-header">
                              <div className="materials-col materials-col-name">Material Name</div>
                              <div className="materials-col materials-col-description">Description</div>
                              <div className="materials-col materials-col-jobcode">Job Code</div>
                              <div className="materials-col materials-col-amount">Amount</div>
                              <div className="materials-col materials-col-action">Action</div>
                            </div>
                            
                            {materialsData.map((material, index) => (
                              <div key={index} className="materials-row">
                                <span className="materials-mobile-only-label">Material name</span>
                                <input
                                  type="text"
                                  className="materials-input materials-col-name"
                                  value={material.name}
                                  onChange={(e) => handleMaterialsChange(index, 'name', e.target.value)}
                                  placeholder="Material name"
                                  aria-label="Material name"
                                />
                                <span className="materials-mobile-only-label">Description</span>
                                <input
                                  type="text"
                                  className="materials-input materials-col-description"
                                  value={material.description}
                                  onChange={(e) => handleMaterialsChange(index, 'description', e.target.value)}
                                  placeholder="Description"
                                  aria-label="Description"
                                />
                                <div className="materials-row-job-amount">
                                  <div className="materials-field-half">
                                    <span className="materials-mobile-only-label">Job code</span>
                                    <input
                                      type="text"
                                      className="materials-input materials-col-jobcode"
                                      value={material.job_code}
                                      onChange={(e) => handleMaterialsChange(index, 'job_code', e.target.value)}
                                      placeholder="Job code"
                                      aria-label="Job code"
                                    />
                                  </div>
                                  <div className="materials-field-half">
                                    <span className="materials-mobile-only-label">Amount</span>
                                    <div className="materials-amount-wrapper materials-col-amount">
                                      <span className="materials-amount-prefix">£</span>
                                      <input
                                        type="number"
                                        className="materials-input materials-amount-input"
                                        value={material.amount}
                                        onChange={(e) => handleMaterialsChange(index, 'amount', e.target.value)}
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                        aria-label="Amount"
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="materials-col-action">
                                  {materialsData.length > 1 && (
                                    <button
                                      type="button"
                                      className="materials-remove-button"
                                      onClick={() => handleRemoveMaterial(index)}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          <button
                            type="button"
                            className="materials-add-button"
                            onClick={handleAddMaterial}
                          >
                            + Add Another Material
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : paymentRequestStep === 4 ? (
                <div className="by-day-form-step">
                  <div className="by-day-form-container">
                    {/* <h3 className="by-day-form-title">Time Sheet</h3> */}
                    
                    <div className="by-day-form-section">
                      <div className="by-day-instructions">
                        <p>Click the box below to select the correct date for the week you are applying for. Use the boxes below to describe the work you have done and your rate for each day.</p>
                        <p>Don't forget to use the 'job code' column to select the site that you are working on.</p>
                        <p>If you have paid for any materials, you can add them below.</p>
                      </div>

                      <div className="form-field-group">
                        <label 
                          className="form-label required"
                          onClick={() => handleDateInputClick(weekCommencingRef)}
                          style={{ cursor: 'pointer' }}
                        >
                          For week commencing:
                        </label>
                        <input
                          ref={weekCommencingRef}
                          type="date"
                          className="form-input"
                          value={byDayFormData.weekCommencing}
                          onChange={(e) => handleByDayWeekChange(e.target.value)}
                          onClick={() => handleDateInputClick(weekCommencingRef)}
                          onFocus={() => handleDateInputClick(weekCommencingRef)}
                          required
                        />
                      </div>

                      <div className="timesheet-table">
                        <div className="timesheet-header">
                          <div className="timesheet-col"></div>
                          <div className="timesheet-col timesheet-col-description">Description of tasks</div>
                          <div className="timesheet-col timesheet-col-jobcode">Job Code</div>
                          <div className="timesheet-col timesheet-col-amount">Amount</div>
                        </div>
                        
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                          <div key={day} className="timesheet-row">
                            <div className="timesheet-day-label">
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </div>
                            <span className="timesheet-mobile-only-label">Description of tasks</span>
                            <input
                              type="text"
                              className="timesheet-input timesheet-col-description"
                              value={byDayFormData.days[day].description}
                              onChange={(e) => handleByDayFormChange(day, 'description', e.target.value)}
                              placeholder="Description"
                              aria-label="Description of tasks"
                            />
                            <div className="timesheet-row-job-amount">
                              <div className="timesheet-field-half">
                                <span className="timesheet-mobile-only-label">Job code</span>
                                <input
                                  type="text"
                                  className="timesheet-input timesheet-col-jobcode"
                                  value={byDayFormData.days[day].jobCode}
                                  onChange={(e) => handleByDayFormChange(day, 'jobCode', e.target.value)}
                                  placeholder="Job Code"
                                  aria-label="Job code"
                                />
                              </div>
                              <div className="timesheet-field-half">
                                <span className="timesheet-mobile-only-label">Amount</span>
                                <div className="timesheet-amount-wrapper">
                                  <span className="timesheet-amount-prefix">£</span>
                                  <input
                                    type="number"
                                    className="timesheet-input timesheet-col-amount"
                                    value={byDayFormData.days[day].amount}
                                    onChange={(e) => handleByDayFormChange(day, 'amount', e.target.value)}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    aria-label="Amount"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Materials Section - Inline */}
                      {showMaterials && (
                        <div ref={byDayMaterialsSectionRef} style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <div>
                            <div style={{ marginBottom: '15px' }}>
                              <h4 style={{ color: 'white', marginBottom: '10px', fontSize: '16px' }}>Materials</h4>
                              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', marginBottom: '15px' }}>
                                Add any materials you have supplied. Tax will not be deducted for materials.
                              </p>
                            </div>
                            <div className="materials-table">
                              <div className="materials-header">
                                <div className="materials-col materials-col-name">Material Name</div>
                                <div className="materials-col materials-col-description">Description</div>
                                <div className="materials-col materials-col-jobcode">Job Code</div>
                                <div className="materials-col materials-col-amount">Amount</div>
                                <div className="materials-col materials-col-action">Action</div>
                              </div>
                              
                              {materialsData.map((material, index) => (
                                <div key={index} className="materials-row">
                                  <span className="materials-mobile-only-label">Material name</span>
                                  <input
                                    type="text"
                                    className="materials-input materials-col-name"
                                    value={material.name}
                                    onChange={(e) => handleMaterialsChange(index, 'name', e.target.value)}
                                    placeholder="Material name"
                                    aria-label="Material name"
                                  />
                                  <span className="materials-mobile-only-label">Description</span>
                                  <input
                                    type="text"
                                    className="materials-input materials-col-description"
                                    value={material.description}
                                    onChange={(e) => handleMaterialsChange(index, 'description', e.target.value)}
                                    placeholder="Description"
                                    aria-label="Description"
                                  />
                                  <div className="materials-row-job-amount">
                                    <div className="materials-field-half">
                                      <span className="materials-mobile-only-label">Job code</span>
                                      <input
                                        type="text"
                                        className="materials-input materials-col-jobcode"
                                        value={material.job_code}
                                        onChange={(e) => handleMaterialsChange(index, 'job_code', e.target.value)}
                                        placeholder="Job code"
                                        aria-label="Job code"
                                      />
                                    </div>
                                    <div className="materials-field-half">
                                      <span className="materials-mobile-only-label">Amount</span>
                                      <div className="materials-amount-wrapper materials-col-amount">
                                        <span className="materials-amount-prefix">£</span>
                                        <input
                                          type="number"
                                          className="materials-input materials-amount-input"
                                          value={material.amount}
                                          onChange={(e) => handleMaterialsChange(index, 'amount', e.target.value)}
                                          placeholder="0.00"
                                          step="0.01"
                                          min="0"
                                          aria-label="Amount"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="materials-col-action">
                                    {materialsData.length > 1 && (
                                      <button
                                        type="button"
                                        className="materials-remove-button"
                                        onClick={() => handleRemoveMaterial(index)}
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            <button
                              type="button"
                              className="materials-add-button"
                              onClick={handleAddMaterial}
                            >
                              + Add Material
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : paymentRequestStep === 5 ? (
                <div className="review-form-step">
                  <div className="review-form-container">
                    <h3 className="review-form-title">Review Payment Request</h3>
                    <p className="review-instruction">Please review all the information below before submitting your payment request.</p>
                    
                    <div className="review-section">
                      <h4 className="review-section-title">Employer Information</h4>
                      <div className="review-item">
                        <span className="review-label">Employer:</span>
                        <span className="review-value">{selectedPaymentEmployer?.employer_name || selectedPaymentEmployer?.employer_email || 'N/A'}</span>
                      </div>
                      <div className="review-item">
                        <span className="review-label">Email:</span>
                        <span className="review-value">{selectedPaymentEmployer?.employer_email || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="review-section">
                      <h4 className="review-section-title">Payment Type</h4>
                      <div className="review-item">
                        <span className="review-label">Type:</span>
                        <span className="review-value">
                          {paymentType === 'job_rate' ? 'Job Rate Payment' : 
                           paymentType === 'by_day' ? 'Day Rate' : 
                           'N/A'}
                        </span>
                      </div>
                    </div>

                    {paymentType === 'job_rate' ? (
                      <div className="review-section">
                        <h4 className="review-section-title">Job Rate Details</h4>
                        <div className="review-item">
                          <span className="review-label">Date work began:</span>
                          <span className="review-value">
                            {jobRateFormData.workStartDate 
                              ? new Date(jobRateFormData.workStartDate).toLocaleDateString()
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="review-item">
                          <span className="review-label">Description:</span>
                          <span className="review-value">
                            {jobRateFormData.description || 'N/A'}
                          </span>
                        </div>
                        <div className="review-item">
                          <span className="review-label">Job Code:</span>
                          <span className="review-value">
                            {jobRateFormData.jobCode || 'N/A'}
                          </span>
                        </div>
                        <div className="review-item">
                          <span className="review-label">Amount:</span>
                          <span className="review-value">
                            {jobRateFormData.amount ? `£${parseFloat(jobRateFormData.amount).toFixed(2)}` : 'N/A'}
                          </span>
                        </div>
                        {/* Labor Total next to job rate details */}
                        {jobRateFormData.amount && parseFloat(jobRateFormData.amount) > 0 && (
                          <div className="review-total">
                            <span className="review-total-label">Labour Total:</span>
                            <span className="review-total-value">£{parseFloat(jobRateFormData.amount).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="review-section">
                        <h4 className="review-section-title">Time Sheet Details</h4>
                        <div className="review-item">
                          <span className="review-label">Week commencing:</span>
                          <span className="review-value">
                            {byDayFormData.weekCommencing 
                              ? new Date(byDayFormData.weekCommencing).toLocaleDateString()
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="review-timesheet">
                          <div className="review-timesheet-header">
                            <div className="review-timesheet-col">Day</div>
                            <div className="review-timesheet-col">Description</div>
                            <div className="review-timesheet-col">Job Code</div>
                            <div className="review-timesheet-col">Amount</div>
                          </div>
                          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                            const dayData = byDayFormData.days[day];
                            const hasData = dayData.description || dayData.jobCode || dayData.amount;
                            if (!hasData) return null;
                            return (
                              <div key={day} className="review-timesheet-row">
                                <div className="review-timesheet-col">
                                  {day.charAt(0).toUpperCase() + day.slice(1)}
                                </div>
                                <div className="review-timesheet-col">
                                  {dayData.description || '-'}
                                </div>
                                <div className="review-timesheet-col">
                                  {dayData.jobCode || '-'}
                                </div>
                                <div className="review-timesheet-col">
                                  {dayData.amount ? `£${parseFloat(dayData.amount).toFixed(2)}` : '-'}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* Labor Total next to timesheet */}
                        {(() => {
                          let laborTotal = 0;
                          Object.values(byDayFormData.days).forEach(dayData => {
                            if (dayData.amount) {
                              laborTotal += parseFloat(dayData.amount) || 0;
                            }
                          });
                          return laborTotal > 0 ? (
                            <div className="review-total">
                              <span className="review-total-label">Labour Total:</span>
                              <span className="review-total-value">£{laborTotal.toFixed(2)}</span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                    
                    {/* Materials Section */}
                    {materialsData.filter(m => m.name || m.description || m.amount || m.job_code).length > 0 && (
                      <div className="review-section">
                        <h4 className="review-section-title">Materials</h4>
                        <div className="review-materials">
                          <div className="review-materials-header">
                            <div className="review-materials-col">Material Name</div>
                            <div className="review-materials-col">Description</div>
                            <div className="review-materials-col">Job Code</div>
                            <div className="review-materials-col">Amount</div>
                          </div>
                          {materialsData
                            .filter(m => m.name || m.description || m.amount || m.job_code)
                            .map((material, index) => (
                              <div key={index} className="review-materials-row">
                                <div className="review-materials-col">{material.name || '-'}</div>
                                <div className="review-materials-col">{material.description || '-'}</div>
                                <div className="review-materials-col">{material.job_code || '-'}</div>
                                <div className="review-materials-col">
                                  {material.amount ? `£${parseFloat(material.amount).toFixed(2)}` : '-'}
                                </div>
                              </div>
                            ))}
                        </div>
                        {/* Materials Total next to materials table */}
                        {(() => {
                          let materialsTotal = 0;
                          materialsData.forEach(m => {
                            if (m.amount) {
                              materialsTotal += parseFloat(m.amount) || 0;
                            }
                          });
                          return materialsTotal > 0 ? (
                            <div className="review-total">
                              <span className="review-total-label">Materials Total:</span>
                              <span className="review-total-value">£{materialsTotal.toFixed(2)}</span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                    
                    {/* Grand Total at the bottom */}
                    {(() => {
                      // Calculate labor total
                      let laborTotal = 0;
                      if (paymentType === 'job_rate' && jobRateFormData.amount) {
                        laborTotal = parseFloat(jobRateFormData.amount) || 0;
                      } else if (paymentType === 'by_day') {
                        Object.values(byDayFormData.days).forEach(dayData => {
                          if (dayData.amount) {
                            laborTotal += parseFloat(dayData.amount) || 0;
                          }
                        });
                      }
                      
                      // Calculate materials total
                      let materialsTotal = 0;
                      materialsData.forEach(m => {
                        if (m.amount) {
                          materialsTotal += parseFloat(m.amount) || 0;
                        }
                      });
                      
                      // Calculate grand total
                      const grandTotal = laborTotal + materialsTotal;
                      
                      return grandTotal > 0 ? (
                        <div className="review-totals-section">
                          <div className="review-total review-total-grand">
                            <span className="review-total-label">Grand Total:</span>
                            <span className="review-total-value">£{grandTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* VAT and CIS Calculation Breakdown */}
                    {(() => {
                      // Calculate labor total
                      let laborTotal = 0;
                      if (paymentType === 'job_rate' && jobRateFormData.amount) {
                        laborTotal = parseFloat(jobRateFormData.amount) || 0;
                      } else if (paymentType === 'by_day') {
                        Object.values(byDayFormData.days).forEach(dayData => {
                          if (dayData.amount) {
                            laborTotal += parseFloat(dayData.amount) || 0;
                          }
                        });
                      }
                      
                      // Calculate materials total
                      let materialsTotal = 0;
                      materialsData.forEach(m => {
                        if (m.amount) {
                          materialsTotal += parseFloat(m.amount) || 0;
                        }
                      });
                      
                      const vatApplicable = String(
                        currentUser?.vat_registered || currentUser?.organization?.vat_registered || ''
                      ).toLowerCase() === 'yes';
                      const vatRate = vatApplicable ? Number(paymentVatRate) : 0;
                      const cisRate = Number(selectedPaymentEmployer?.tax_rate ?? 30);
                      
                      // Calculate gross amount (L + M)
                      const grossAmount = laborTotal + materialsTotal;
                      
                      // Calculate VAT amount (on gross_amount if vat_applicable)
                      const vatAmount = vatApplicable && grossAmount > 0 ? grossAmount * (vatRate / 100.0) : 0;
                      
                      // Calculate CIS amount (on labor only)
                      const cisAmount = laborTotal > 0 && cisRate > 0 ? laborTotal * (cisRate / 100.0) : 0;
                      
                      // Calculate total payable (gross + VAT - CIS)
                      const totalPayable = grossAmount + vatAmount - cisAmount;
                      
                      // Only show if there's a calculation to display
                      if (grossAmount > 0) {
                        return (
                          <div className="review-section">
                            {vatApplicable && (
                              <div style={{ marginBottom: '20px' }}>
                                <h4 className="review-section-title">VAT</h4>
                                <div className="form-field-group" style={{ marginBottom: '12px' }}>
                                  <label className="form-label">VAT rate for this payment</label>
                                  <select
                                    className="form-input"
                                    value={paymentVatRate}
                                    onChange={(e) => setPaymentVatRate(Number(e.target.value))}
                                  >
                                    <option value={20}>20% (Standard)</option>
                                    <option value={5}>5% (Reduced)</option>
                                    <option value={0}>0% (Zero rated)</option>
                                  </select>
                                </div>
                                <div className="form-field-group">
                                  <label className="form-label">
                                    I believe Domestic Reverse Charge VAT may apply to this payment
                                  </label>
                                  <select
                                    className="form-input"
                                    value={reverseChargeSuggestion ? 'yes' : 'no'}
                                    onChange={(e) => setReverseChargeSuggestion(e.target.value === 'yes')}
                                  >
                                    <option value="no">No</option>
                                    <option value="yes">Yes</option>
                                  </select>
                                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', marginTop: '8px', lineHeight: 1.5 }}>
                                    Reverse charge may apply where both parties are VAT registered and construction services are supplied onward by the contractor. The contractor will confirm the final VAT treatment during approval.
                                  </p>
                                </div>
                              </div>
                            )}
                            <h4 className="review-section-title">Payment Calculation</h4>
                            <div className="review-item">
                              <span className="review-label">Gross Amount (Labour + Materials):</span>
                              <span className="review-value">£{grossAmount.toFixed(2)}</span>
                            </div>
                            {vatApplicable && (
                              <div className="review-item">
                                <span className="review-label">VAT ({vatRate}%):</span>
                                <span className="review-value">£{vatAmount.toFixed(2)}</span>
                              </div>
                            )}
                            {cisRate > 0 && laborTotal > 0 && (
                              <div className="review-item">
                                <span className="review-label">CIS Deduction ({cisRate}% on Labour):</span>
                                <span className="review-value">-£{cisAmount.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="review-totals-section" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '2px solid rgba(255, 255, 255, 0.2)' }}>
                              <div className="review-total review-total-grand">
                                <span className="review-total-label">Net Payable:</span>
                                <span className="review-total-value">£{totalPayable.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Verification Checkbox */}
                    <div className="review-verification">
                      <label className="review-verification-label">
                        <input
                          type="checkbox"
                          checked={verificationChecked}
                          onChange={(e) => setVerificationChecked(e.target.checked)}
                          className="review-verification-checkbox"
                        />
                        <span className="review-verification-text">
                          I verify that all the information I have provided is correct and accurate. I understand that submitting incorrect information may result in delays or rejection of my payment request.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            {paymentRequestStep === 2 && (
              <div className="modal-footer">
                <button
                  className="back-button"
                  onClick={() => setPaymentRequestStep(1)}
                >
                  Back
                </button>
                <button
                  className="continue-button"
                  onClick={handlePaymentTypeContinue}
                  disabled={!paymentType}
                >
                  CONTINUE
                </button>
              </div>
            )}
            {paymentRequestStep === 3 && (
              <div className="modal-footer">
                {!showMaterials ? (
                  <button
                    type="button"
                    onClick={handleShowMaterials}
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(91, 192, 222, 0.5)',
                      color: '#57bedd',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Add Material
                  </button>
                ) : (
                  <div></div>
                )}
                <div style={{ display: 'flex', gap: '15px' }}>
                  <button
                    className="back-button"
                    onClick={() => {
                      setPaymentRequestStep(2);
                      setShowMaterials(false); // Reset materials visibility when going back
                    }}
                  >
                    Back
                  </button>
                  <button
                    className="continue-button"
                    onClick={handleJobRateFormSubmit}
                    disabled={!jobRateFormData.workStartDate}
                  >
                    CONTINUE
                  </button>
                </div>
              </div>
            )}
            {paymentRequestStep === 4 && (
              <div className="modal-footer">
                {!showMaterials ? (
                  <button
                    type="button"
                    onClick={handleShowMaterials}
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(91, 192, 222, 0.5)',
                      color: '#57bedd',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Add Material
                  </button>
                ) : (
                  <div></div>
                )}
                <div style={{ display: 'flex', gap: '15px' }}>
                  <button
                    className="back-button"
                    onClick={() => {
                      setPaymentRequestStep(2);
                      setShowMaterials(false); // Reset materials visibility when going back
                    }}
                  >
                    Back
                  </button>
                  <button
                    className="continue-button"
                    onClick={handleByDayFormSubmit}
                    disabled={!byDayFormData.weekCommencing}
                  >
                    CONTINUE
                  </button>
                </div>
              </div>
            )}
            {paymentRequestStep === 5 && (
              <div className="modal-footer" style={{ flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '15px', width: '100%' }}>
                  <button
                    className="back-button"
                    onClick={() => {
                      if (paymentType === 'job_rate') {
                        setPaymentRequestStep(3);
                      } else {
                        setPaymentRequestStep(4);
                      }
                    }}
                  >
                    Back
                  </button>
                  <button
                    className="continue-button"
                    onClick={handleFinalSubmit}
                    disabled={requestLoading || !verificationChecked || !isBankDetailsComplete}
                  >
                    {requestLoading ? 'SUBMITTING...' : 'SUBMIT'}
                  </button>
                </div>
                {/* Display success/error message under submit button */}
                {message.text && (
                  <div className={`message ${message.type}`} style={{ 
                    marginTop: '15px', 
                    width: '100%', 
                    textAlign: 'center' 
                  }}>
                    {message.text}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Payment Detail Modal */}
      {isPaymentDetailModalOpen && selectedPayment && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => {
          setIsPaymentDetailModalOpen(false);
          setSelectedPayment(null);
        }}>
          <div className="modal-content payment-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Payment Request Details</h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {(selectedPayment.status === 'approved' || selectedPayment.status === 'paid') && (
                  <button
                    onClick={() => handleDownloadPaymentPDF(selectedPayment.payment_request_id)}
                    style={{
                      background: '#28a745',
                      border: '1px solid #28a745',
                      color: '#ffffff',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.3s',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#218838';
                      e.target.style.borderColor = '#218838';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#28a745';
                      e.target.style.borderColor = '#28a745';
                    }}
                    title="Download Invoice PDF"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Download PDF
                  </button>
                )}
                <button className="modal-close" onClick={() => {
                  setIsPaymentDetailModalOpen(false);
                  setSelectedPayment(null);
                }}>×</button>
              </div>
            </div>
            <div className="modal-body">
              <div className="payment-detail-section">
                <h4>Employer Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Name:</span>
                    <span className="payment-detail-value">{selectedPayment.employer_name || 'N/A'}</span>
                  </div>
                  {selectedPayment.employer_email && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Email:</span>
                      <span className="payment-detail-value">{selectedPayment.employer_email}</span>
                    </div>
                  )}
                  {selectedPayment.employer_phone && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Phone:</span>
                      <span className="payment-detail-value">{selectedPayment.employer_phone}</span>
                    </div>
                  )}
                  {selectedPayment.employer_company_name && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Company Name:</span>
                      <span className="payment-detail-value">{selectedPayment.employer_company_name}</span>
                    </div>
                  )}
                  {selectedPayment.employer_utr && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">UTR:</span>
                      <span className="payment-detail-value">{selectedPayment.employer_utr}</span>
                    </div>
                  )}
                  {selectedPayment.employer_ni_number && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">NI Number:</span>
                      <span className="payment-detail-value">{selectedPayment.employer_ni_number}</span>
                    </div>
                  )}
                  {(selectedPayment.employer_building_name_no || selectedPayment.employer_street) && (
                    <div className="payment-detail-item" style={{ gridColumn: '1 / -1' }}>
                      <span className="payment-detail-label">Address:</span>
                      <span className="payment-detail-value">
                        {[
                          selectedPayment.employer_building_name_no,
                          selectedPayment.employer_street
                        ].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {selectedPayment.employer_city && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">City:</span>
                      <span className="payment-detail-value">{selectedPayment.employer_city}</span>
                    </div>
                  )}
                  {selectedPayment.employer_postal_code && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Postal Code:</span>
                      <span className="payment-detail-value">{selectedPayment.employer_postal_code}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="payment-detail-section">
                <h4>Payment Information</h4>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Payment Type:</span>
                  <span className="payment-detail-value">
                    {selectedPayment.payment_type === 'job_rate' ? 'Job Rate' : 'Day Rate'}
                  </span>
                </div>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Status:</span>
                  <span className={`status-badge status-${selectedPayment.status}`}>
                    {selectedPayment.status === 'approved' ? 'To be paid' : selectedPayment.status ? selectedPayment.status.charAt(0).toUpperCase() + selectedPayment.status.slice(1) : 'Pending'}
                  </span>
                </div>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Submitted Date:</span>
                  <span className="payment-detail-value">
                    {selectedPayment.submitted_at 
                      ? new Date(selectedPayment.submitted_at).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
                {selectedPayment.approved_at && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">To be paid Date:</span>
                    <span className="payment-detail-value">
                      {new Date(selectedPayment.approved_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {selectedPayment.rejected_at && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Rejected Date:</span>
                    <span className="payment-detail-value">
                      {new Date(selectedPayment.rejected_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {selectedPayment.paid_at && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Paid Date:</span>
                    <span className="payment-detail-value">
                      {new Date(selectedPayment.paid_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {selectedPayment.payment_type === 'job_rate' ? (
                <div className="payment-detail-section">
                  <h4>Job Rate Details</h4>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Work Start Date:</span>
                    <span className="payment-detail-value">
                      {selectedPayment.work_start_date 
                        ? new Date(selectedPayment.work_start_date).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Description:</span>
                    <span className="payment-detail-value">{selectedPayment.description || 'N/A'}</span>
                  </div>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Job Code:</span>
                    <span className="payment-detail-value">{selectedPayment.job_code || 'N/A'}</span>
                  </div>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Amount:</span>
                    <span className="payment-detail-value">
                      £{selectedPayment.amount ? parseFloat(selectedPayment.amount).toFixed(2) : '0.00'}
                    </span>
                  </div>
                  {/* Labor Total next to job rate details */}
                  {selectedPayment.labor_total !== undefined && selectedPayment.labor_total !== null && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Labour Total:</span>
                      <span className="payment-detail-value">
                        £{parseFloat(selectedPayment.labor_total).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="payment-detail-section">
                  <h4>Time Sheet Details</h4>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Week Commencing:</span>
                    <span className="payment-detail-value">
                      {selectedPayment.week_commencing 
                        ? new Date(selectedPayment.week_commencing).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                  {selectedPayment.days_data && (
                    <div className="payment-timesheet">
                      <div className="payment-timesheet-header">
                        <div className="payment-timesheet-col">Day</div>
                        <div className="payment-timesheet-col">Description</div>
                        <div className="payment-timesheet-col">Job Code</div>
                        <div className="payment-timesheet-col">Amount</div>
                      </div>
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                        const dayData = selectedPayment.days_data[day];
                        if (!dayData || (!dayData.description && !dayData.jobCode && !dayData.amount)) return null;
                        return (
                          <div key={day} className="payment-timesheet-row">
                            <div className="payment-timesheet-col">
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </div>
                            <div className="payment-timesheet-col">{dayData.description || '-'}</div>
                            <div className="payment-timesheet-col">{dayData.jobCode || '-'}</div>
                            <div className="payment-timesheet-col">
                              {dayData.amount ? `£${parseFloat(dayData.amount).toFixed(2)}` : '-'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Labor Total next to timesheet */}
                  {selectedPayment.labor_total !== undefined && selectedPayment.labor_total !== null && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Labour Total:</span>
                      <span className="payment-detail-value">
                        £{parseFloat(selectedPayment.labor_total).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {selectedPayment.materials_data && selectedPayment.materials_data.length > 0 && (
                <div className="payment-detail-section">
                  <h4>Materials</h4>
                  <div className="payment-timesheet materials-table">
                    <div className="payment-timesheet-header">
                      <div className="payment-timesheet-col">Material Name</div>
                      <div className="payment-timesheet-col">Description</div>
                      <div className="payment-timesheet-col">Job Code</div>
                      <div className="payment-timesheet-col">Amount</div>
                    </div>
                    {selectedPayment.materials_data.map((material, index) => (
                      <div key={index} className="payment-timesheet-row">
                        <div className="payment-timesheet-col">{material.name || '-'}</div>
                        <div className="payment-timesheet-col">{material.description || '-'}</div>
                        <div className="payment-timesheet-col">{material.job_code || material.jobCode || '-'}</div>
                        <div className="payment-timesheet-col">
                          {material.amount ? `£${parseFloat(material.amount).toFixed(2)}` : '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Materials Total next to materials table */}
                  {selectedPayment.materials_total !== undefined && selectedPayment.materials_total !== null && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Materials Total:</span>
                      <span className="payment-detail-value">
                        £{parseFloat(selectedPayment.materials_total).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Calculation Breakdown */}
              {(selectedPayment.gross_amount || selectedPayment.total_payable) && (
                <div className="payment-detail-section">
                  <h4>Payment Calculation</h4>
                  {selectedPayment.gross_amount !== undefined && selectedPayment.gross_amount !== null && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Gross Amount (Labour + Materials):</span>
                      <span className="payment-detail-value">
                        £{parseFloat(selectedPayment.gross_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedPayment.vat_applicable && selectedPayment.vat_amount !== undefined && selectedPayment.vat_amount !== null && selectedPayment.vat_amount > 0 && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">VAT ({selectedPayment.vat_rate || 20}%):</span>
                      <span className="payment-detail-value">
                        £{parseFloat(selectedPayment.vat_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedPayment.reverse_charge_applies && (
                    <>
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">VAT Treatment:</span>
                        <span className="payment-detail-value">Domestic Reverse Charge</span>
                      </div>
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">VAT Paid to Subcontractor:</span>
                        <span className="payment-detail-value">
                          £{parseFloat(selectedPayment.vat_paid_to_subcontractor || 0).toFixed(2)}
                        </span>
                      </div>
                      <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', marginTop: '8px', lineHeight: 1.5 }}>
                        Domestic Reverse Charge applies. The contractor is responsible for accounting for VAT to HMRC.
                      </p>
                    </>
                  )}
                  {selectedPayment.cis_rate > 0 && selectedPayment.cis_amount !== undefined && selectedPayment.cis_amount !== null && selectedPayment.cis_amount > 0 && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">CIS Deduction ({selectedPayment.cis_rate}% on Labour):</span>
                      <span className="payment-detail-value">
                        -£{parseFloat(selectedPayment.cis_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedPayment.total_payable !== undefined && selectedPayment.total_payable !== null && (
                    <div className="payment-detail-item" style={{ 
                      borderTop: '2px solid rgba(255, 255, 255, 0.2)', 
                      paddingTop: '15px',
                      marginTop: '10px'
                    }}>
                      <span className="payment-detail-label" style={{ fontWeight: '700', fontSize: '16px' }}>Net Payable:</span>
                      <span className="payment-detail-value" style={{ fontWeight: '700', fontSize: '16px' }}>
                        £{parseFloat(selectedPayment.total_payable).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {/* Fallback to Grand Total if total_payable is not available */}
                  {(!selectedPayment.total_payable && selectedPayment.total_amount) && (
                    <div className="payment-detail-item" style={{ 
                      borderTop: '2px solid rgba(255, 255, 255, 0.2)', 
                      paddingTop: '15px',
                      marginTop: '10px'
                    }}>
                      <span className="payment-detail-label" style={{ fontWeight: '700', fontSize: '16px' }}>Grand Total:</span>
                      <span className="payment-detail-value" style={{ fontWeight: '700', fontSize: '16px' }}>
                        £{parseFloat(selectedPayment.total_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {selectedPayment.employer_comment && (
                <div className="payment-detail-section">
                  <h4>Employer Comment</h4>
                  <p className="payment-comment">{selectedPayment.employer_comment}</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Withdraw Confirmation Modal */}
      {isWithdrawConfirmModalOpen && paymentToWithdraw && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={cancelWithdrawPayment}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Withdraw Payment Request</h2>
              <button className="modal-close" onClick={cancelWithdrawPayment}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.6' }}>
                Are you sure you want to withdraw this payment request?
              </p>
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.1)', 
                padding: '15px', 
                borderRadius: '8px', 
                marginBottom: '20px' 
              }}>
                <p style={{ margin: '5px 0', color: 'rgba(255, 255, 255, 0.9)' }}>
                  <strong>Employer:</strong> {paymentToWithdraw.employer_name || 'N/A'}
                </p>
                <p style={{ margin: '5px 0', color: 'rgba(255, 255, 255, 0.9)' }}>
                  <strong>Payment Type:</strong> {paymentToWithdraw.payment_type === 'job_rate' ? 'Job Rate' : 'Day Rate'}
                </p>
                <p style={{ margin: '5px 0', color: 'rgba(255, 255, 255, 0.9)' }}>
                  <strong>Net Payable:</strong> {formatPaymentNetPayable(paymentToWithdraw)}
                </p>
              </div>
              <p style={{ color: '#f44336', fontSize: '14px', marginTop: '10px' }}>
                <strong>Warning:</strong> This action cannot be undone. The employer will be notified of this withdrawal.
              </p>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
              <button
                className="back-button"
                onClick={cancelWithdrawPayment}
                disabled={withdrawLoading}
              >
                Cancel
              </button>
              <button
                className="continue-button"
                onClick={confirmWithdrawPayment}
                disabled={withdrawLoading}
                style={{
                  background: withdrawLoading ? '#999' : '#f44336',
                  borderColor: withdrawLoading ? '#999' : '#f44336',
                  cursor: withdrawLoading ? 'not-allowed' : 'pointer',
                  opacity: withdrawLoading ? 0.6 : 1
                }}
              >
                {withdrawLoading ? 'Withdrawing...' : 'Withdraw'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SubcontractorDashboard;
